import React from 'react';

import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import type { CppInvoiceTotals } from '~/services/LuxLoyalty';

import { currencyCode, formatCurrencyCents, formatSignedPoints } from './invoiceFormat';

interface Props {
  totals: CppInvoiceTotals;
  currency: string | null;
  /** Omit the money figure when the rows are not all in one currency. */
  omitCost?: boolean;
}

/** One labelled figure in the summary strip. */
function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

/**
 * Whole-period figures shown above the transactions grid.
 *
 * These are computed by the service across the entire period, not the visible page — a row's cost
 * depends on the other rows landing on its day, so it cannot be totalled per page. They therefore
 * stay put as the user pages through the grid.
 */
export default function InvoiceTotalsSummary({ totals, currency, omitCost = false }: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap alignItems="flex-start">
        <Figure label="Points issued" value={formatSignedPoints(totals.creditPoints)} />
        <Figure label="Points debited" value={formatSignedPoints(totals.debitPoints)} />
        <Figure label="Points reversed" value={formatSignedPoints(totals.refundPoints)} />
        <Figure label="Bonus points issued" value={formatSignedPoints(totals.creditBonusPoints)} />
        <Figure label="Bonus points debited" value={formatSignedPoints(totals.debitBonusPoints)} />
        <Figure label="Bonus points reversed" value={formatSignedPoints(totals.refundBonusPoints)} />
        <Figure label="Net billable points" value={formatSignedPoints(totals.netPoints)} />
        <Figure label="Net billable bonus points" value={formatSignedPoints(totals.netBonusPoints)} />
        {!omitCost && (
          <Figure
            label={`Total cost (${currencyCode(currency)})`}
            value={formatCurrencyCents(totals.amountCents, currency)}
          />
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ alignSelf: 'center' }}>
          {totals.pendingTransactionCount > 0 && (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={`${totals.pendingTransactionCount} pending — figures will still move`}
            />
          )}
          {totals.unpricedNetPoints !== 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${formatSignedPoints(totals.unpricedNetPoints)} points unpriced`}
            />
          )}
          {totals.unpricedNetBonusPoints !== 0 && (
            <Chip
              size="small"
              color="error"
              variant="outlined"
              label={`${formatSignedPoints(totals.unpricedNetBonusPoints)} bonus points unpriced`}
            />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}
